import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Icon, Select, Toolbar, ToolbarItem } from '../common';
import styled from 'styled-components';
import AWSPortOpeningRequest from '../../models/entities/AWSPortOpeningRequests';
import { PORFilters } from './AWSPortOpeningRequestsHome';
import Filter from '../common/tables/Filter';
import { useNavigate } from 'react-router-dom';
import CheckboxFilter from '../common/tables/CheckboxFilter';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import Memoized from '../common/Memoized';
import moment from 'moment';
import DateRangePicker from '../common/DateRangePicker';
import { AWSPortOpeningRequestsTypes } from '../../models/enums/POR/AWSPortOpeningRequestType';
import { Searchbar } from '../common/components/Searchbar';
import { WorkflowState } from '../../models/enums/WorkflowState';

const MyP = styled.p`
	margin-right: 8px;
	margin-bottom: auto !important;
	margin-top: auto !important;
`;

const MyLink = styled.span`
	color: #0c69b0;
	text-decoration: underline;
	cursor: pointer;
`;

interface PORByRequestFilterProps {
	pors: AWSPortOpeningRequest[];
	setFilteredRequests: any;
}

export type AWSPorsMemoizedData = {
	searchUser: void,
    status: void,
    scope: void,
    type: void,
    orderBy: void,
    fromDate: void,
    toDate: void
}

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
    type: undefined,
	status: undefined,
	fromDate: undefined,
	toDate: undefined,
} as AWSPorsMemoizedData;

const memoized = new Memoized(defaultMemoizedDataStructure);

const AWSPortOpeningRequestsHomeFilters = ({ pors, setFilteredRequests }: PORByRequestFilterProps) => {
	const navigate = useNavigate();

	const getDefaultFilters = () => {
		const defaultFilters = {
			searchUser: '',
			status: [
				{ label: 'Submitted', value: WorkflowState.Submitted.name, checked: true },
                { label: 'Pending for Account Owner Approval', value: WorkflowState.PendingAccountOwnerApproval.name, checked: true },
				{ label: 'Pending for Network Policy Approval', value: WorkflowState.PendingNetworkPolicyApproval.name, checked: true },
				{ label: 'Pending for Network Policy Solution Architect Approval', value: WorkflowState.PendingSolutionArchitectureApproval.name, checked: true },
				{ label: 'Pending for Cyber Approval', value: WorkflowState.PendingForCyberApproval.name, checked: true },
				{ label: 'Pending for DOAG Approval', value: WorkflowState.PendingForDOAGApproval.name, checked: true },
                { label: 'Pending for Application Approval', value: WorkflowState.PendingApplicationApproval.name, checked: true },
				{ label: 'Awaiting Implementation', value: WorkflowState.AwaitingImplementation.name, checked: true },
				{ label: 'Completed', value: WorkflowState.Completed.name, checked: true },
				{ label: 'Draft', value: WorkflowState.Draft.name, checked: true },
				{ label: 'Review', value: WorkflowState.Review.name, checked: true },
				{ label: 'Rejected', value: WorkflowState.Rejected.name, checked: true },
				{ label: 'Cancelled', value: WorkflowState.Cancelled.name, checked: true },
			],
			scope: [
					{ label: 'trusted -> Internet', value: PortOpeningRequestsDirection.TrustedToInternet.name, checked: true },
                    { label: 'internet -> DMZ(North)', value: PortOpeningRequestsDirection.InternetToDMZNorth.name, checked: true },
                    { label: 'DMZ -> TrustedL4(South)', value: PortOpeningRequestsDirection.DMZTrustedToL4South.name, checked: true },
                    { label: 'DMZ -> Internet(South + Obi)', value: PortOpeningRequestsDirection.DMZToInternetSouthObi.name, checked: true },
                    
                    { label: 'GLLOnPrem <-> GLLAWS', value: PortOpeningRequestsDirection.GLLOnPremToGLLAWS.name, checked: true },
                    { label: 'GLLAWS <-> GLLAWS', value: PortOpeningRequestsDirection.GLLAWSToGLLAWS.name, checked: true },
                    { label: 'GLLAWS <-> GLLUIZL4', value: PortOpeningRequestsDirection.GLLAWSToGLUizL4.name, checked: true },
                    { label: 'Untrusted <-> L4', value: PortOpeningRequestsDirection.UntrustedToL4.name, checked: true },
                    { label: 'Untrusted <-> Internet', value: PortOpeningRequestsDirection.UntrustedToInternet.name, checked: true },
                    { label: 'External Accounts', value: PortOpeningRequestsDirection.ExternalAccounts.name, checked: true }
				],
            type: [
                {label: "Untrusted", value: AWSPortOpeningRequestsTypes.Untrusted.name, checked: true },
                {label: "Account -> Internet", value: AWSPortOpeningRequestsTypes.AccountToInternet.name, checked: true },
                {label: "Account -> DMZ", value: AWSPortOpeningRequestsTypes.AccountToDMZ.name, checked: true },
            ],    
            orderBy: "newest",
            fromDate: undefined,
            toDate: undefined

		} as PORFilters;

		return defaultFilters;
	};

	const getFilters = () => {
		const defaultFilters = getDefaultFilters();

		const filters = {
			searchUser: memoized.getMemoizedData("searchUser", defaultFilters.searchUser),
			status: memoized.getMemoizedData("status",defaultFilters.status),
			scope: memoized.getMemoizedData("scope",defaultFilters.scope),
            orderBy: memoized.getMemoizedData("oderBy", defaultFilters.orderBy),
            fromDate: memoized.getMemoizedData("fromDate", defaultFilters.fromDate),
            toDate: memoized.getMemoizedData("toDate", defaultFilters.toDate),
            type: memoized.getMemoizedData("type", defaultFilters.type)
		} as PORFilters;

		return filters;
	};

	const allFiltersChecked = (key: string) => {
		return getFilters()[key].reduce((acc, box) => acc && box.checked, true);
	};

    const saveFilters = () => {
        memoized.saveMemoizeData("status", filter.filters.status);
        memoized.saveMemoizeData("searchUser", filter.filters.searchUser)
        memoized.saveMemoizeData("orderBy", filter.filters.orderBy)
        memoized.saveMemoizeData("scope", filter.filters.scope)
        memoized.saveMemoizeData("fromDate", filter.filters.fromDate)
        memoized.saveMemoizeData("toDate", filter.filters.toDate)
        memoized.saveMemoizeData("type",filter.filters.type)
    }

	const filterPORs = () => {
		let filteredRequests = [];

		saveFilters();

		const filterCallback = (por: AWSPortOpeningRequest) => {
			return (
				filter.filterCheckBox(
					'status',
					por.workflowInstance.currentWorkflowInstanceState.workflowState?.name
				) &&
				filter.filterCheckBox('scope', por.direction.name) &&
                filter.filterCheckBox('type', por.type?.name) && 
                filter.isInDateRange("fromDate", "toDate",new Date(por.workflowInstance.createdDate)) &&
				(filter.filterSearchText('searchUser', por.workflowInstance.createdBy.displayName) ||
					filter.filterSearchText('searchUser', por.contactUser?.displayName) ||
					filter.filterSearchText('searchUser', por.id.toLocaleString()))
			);
		};

		filteredRequests = pors.filter(filterCallback).sort((a, b) => {
			if (filter.filters.orderBy == undefined || filter.filters.orderBy === 'newest') {
				return a.id < b.id ? 1 : -1;
			}
			return a.id > b.id ? 1 : -1;
		});

		setFilteredRequests(filteredRequests);
	};

	const [filter] = useState<Filter<PORFilters>>(new Filter(filterPORs, getFilters()));

	const clearFilter = () => {
		memoized.resetAllMemoizeData();
		filter.updateFilters(getFilters());
	};

	useEffect(() => {
		filter.updateFilterFunction(filterPORs);
	}, [pors]);

	useEffect(() => {
		filter.filter();
	}, []);

	const goToNewRequest = () => {
		navigate('/awsPortOpeningRequest/new');
	};

	const clearSearchbar = (e) => {
		e.preventDefault();
		filter.updateFilter('', 'searchUser');
	};

    return (
        <>
            {filter && (
                <>
                    <Toolbar>
                        <ToolbarItem width="xl">
                            <Searchbar
                                condensed
                                onClick={clearSearchbar}
                                value={filter.filters.searchUser}
                                onChange={(e) => filter.handleSearchTextChange(e, 'searchUser')}
                                placeholder={ "Search all fields shown below (i.e. specific IP address, Description, etc.)"}
                                />
                        </ToolbarItem>
                        <ToolbarItem right>
                            <Button
                                variant="primary"
                                size="small"
                                onClick={() => goToNewRequest()}
                            >
                                <Icon size="small" icon="plus-filled" />
                                <span>New Request</span>
                            </Button>
                        </ToolbarItem>
                    </Toolbar>
                    <Toolbar>
                    <ToolbarItem>
                        <DateRangePicker
                            toDateOnChangeFunction={(e) => filter.handleSearchTextChange(e,'toDate')}
                            fromDateOnChangeFunction={(e) => filter.handleSearchTextChange(e,'fromDate')}	
                            fromDateValue={filter.filters.fromDate}
                            toDateValue={filter.filters.toDate}
                            containerStyle={{position:"relative",bottom:"8px"}}
                            pickerStyle={{margin:"0px"}} 
                        />
                    </ToolbarItem>
                        <ToolbarItem>
                            <CheckboxFilter
                                label="Type"
                                buttonProps={{ size: 'small' }}
                                onChange={(event) => filter.handleCheckboxChange(event, 'type')}
                                options={filter.filters.type} 
                                allDefaultChecked={allFiltersChecked("type")}/>
                        </ToolbarItem>
                        <ToolbarItem>
                            <CheckboxFilter
                                label="Scope"
                                buttonProps={{ size: 'small' }}
                                onChange={(event) => filter.handleCheckboxChange(event, 'scope')}
                                options={filter.filters.scope} 
                                allDefaultChecked={allFiltersChecked("scope")}/>
                        </ToolbarItem>
                        <ToolbarItem>
                            <CheckboxFilter
                                label="Status"
                                onChange={(event) => filter.handleCheckboxChange(event, 'status')}
                                options={filter.filters.status} 
                                allDefaultChecked={allFiltersChecked("status")}/>
                        </ToolbarItem>
                        <ToolbarItem>
                            <MyLink onClick={() => clearFilter()}>Clear filters</MyLink>
                        </ToolbarItem>
                        <ToolbarItem right>
                            <ButtonGroup>
                                <MyP>Sort by</MyP>
                                <Select
                                    value={filter.filters.orderBy}
                                    onChange={(e) => {filter.handleSearchTextChange(e, 'orderBy');}}
                                    options={[
                                        { text: 'Newest', value: 'newest' },
                                        { text: 'Oldest', value: 'oldest' },
                                    ]}
                                />
                            </ButtonGroup>
                        </ToolbarItem>
                    </Toolbar>
                </>
            )}
        </>
    );
};

export default AWSPortOpeningRequestsHomeFilters;
